import VendorCard from "@/components/MultiVendorSelector/VendorCard";
import Column from "@/components/Ui/Column";
import Icon from "@/components/Ui/Icon";
import Row from "@/components/Ui/Row";
import TextInput from "@/components/Ui/TextInput";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Vendor, useVendorContext } from "@/contexts/vendor-context";
import useOnClickOutside from "@/hooks/use-click-outside";
import { ChevronDown } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";

const filterVendors = (vendors: Vendor[], text: string) =>
  text
    ? vendors.filter(
        ({ vendorId, name }) =>
          vendorId.includes(text.toLocaleLowerCase()) ||
          (name || "").toLocaleLowerCase().includes(text.toLocaleLowerCase()),
      )
    : vendors;

type VendorCardListProps = {
  selectedVendors: string[];
  setSelectedVendors: (vendors: string[]) => void;
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
};

const Index = ({
  selectedVendors,
  setSelectedVendors,
  selectedCurrency,
  setSelectedCurrency,
}: VendorCardListProps) => {
  const {
    vendors = [],
    getVendorSettings,
    multiVendorKey,
  } = useVendorContext();
  const [textFilter, setTextFilter] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);
  const vendorsWithSettings = vendors
    .map(getVendorSettings)
    .filter((vendor): vendor is Vendor => vendor !== undefined)
    .filter(({ vendorId }) => vendorId !== multiVendorKey);
  const [filteredVendors, setFilteredVendors] = useState(vendorsWithSettings);
  const wrapperRef = useRef(null);

  const filterList = (e: ChangeEvent<HTMLInputElement>) => {
    setTextFilter(e.target.value);
    setFilteredVendors(filterVendors(vendorsWithSettings, e.target.value));
  };

  useOnClickOutside(wrapperRef, () => {
    setShowVendorList(false);
  });

  const getInputPlaceholder = () => {
    if (showVendorList) {
      return "Type to filter";
    }
    if (!selectedVendors.length) {
      return "Select a vendor";
    }
    if (selectedVendors.length && selectedVendors.length <= 3) {
      return selectedVendors
        .map(
          (v) =>
            vendorsWithSettings.find((vendor) => vendor.vendorId === v)?.name ||
            v,
        )
        .join(", ");
    }
    if (selectedVendors.length === vendorsWithSettings.length) {
      return "All vendors selected";
    }
    return `${selectedVendors.length} vendors selected`;
  };

  const availableCurrencies = [
    ...new Set(
      vendorsWithSettings
        .filter((vendor) => selectedVendors.includes(vendor.vendorId))
        .map((vendor) => vendor.currency),
    ),
  ].filter((currency) => currency) as string[];

  const setUnsetCurrency = (currency: string) => {
    if (selectedCurrency == currency) {
      setSelectedCurrency("");
    }
    if (selectedCurrency != currency) {
      setSelectedCurrency(currency);
    }
  };

  return (
    <Row left className="justify-between! gap-4">
      <div className="relative w-[400px]" ref={wrapperRef}>
        <TextInput
          name="vendor-filter"
          tabIndex={0}
          value={textFilter}
          onChange={filterList}
          placeholder={getInputPlaceholder()}
          onFocus={() => setShowVendorList(true)}
          slim
          autoComplete="off"
        />
        {showVendorList && (
          <div className="absolute top-full -right-[100px] left-0 z-10 mt-2">
            <div className="bg-card rounded p-2 shadow-lg">
              <div className="custom-scrollbar custom-inline-scrollbar max-h-[calc(100vh-160px)] overflow-y-auto pb-2">
                <Row left className="gap-2 border-b p-2 pb-4">
                  <Button
                    className="px-3 py-2 text-sm!"
                    onClick={() =>
                      setSelectedVendors(filteredVendors.map((v) => v.vendorId))
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    className="px-3 py-2 text-sm!"
                    onClick={() => setSelectedVendors([])}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outline"
                    className="ml-auto p-2 text-sm!"
                    onClick={() => setShowVendorList(false)}
                    title="Close"
                    aria-label="Close"
                  >
                    <Icon name="cross" />
                  </Button>
                </Row>
                <Column>
                  {filteredVendors.map((vendor) => (
                    <VendorCard
                      vendor={vendor}
                      key={vendor.vendorId}
                      selectedVendors={selectedVendors}
                      setSelectedVendors={setSelectedVendors}
                    />
                  ))}
                </Column>
              </div>
            </div>
          </div>
        )}
      </div>
      {availableCurrencies.length > 1 && (
        <div className="mx-1">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <Button aria-label="Open menu" data-testid="user-actions">
                Select currency <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-70" align="end">
              <DropdownMenuGroup>
                {availableCurrencies.map((currency) => (
                  <DropdownMenuItem
                    key={currency}
                    onSelect={() => setUnsetCurrency(currency)}
                    className="row justify-between!"
                  >
                    {currency}
                    <span className="bg-input/30 row aspect-square w-7 rounded border">
                      {selectedCurrency == currency && (
                        <Icon className="text-[14px]" name="checkmark" />
                      )}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </Row>
  );
};

export default Index;
